import { FilterToggleButton, FilterToggleButtonGroup } from "@/components/utils/FilterToggleButtons";
import { FunnelIcon } from "@/components/utils/icons/FunnelIcon";
import LazoTooltip from "@/components/utils/LazoTooltip";
import { Colors } from "@/enums/Colors";
import { ChatActivityFilter, ChatDateFilter } from "@/services/ai-chat/LazoChatConversationService";
import { CloseOutlined } from "@mui/icons-material";
import { Fade, Popper, styled, tooltipClasses, Typography, typographyClasses } from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type FilterMenuProps = {
  setActivityFilter: (filter: ChatActivityFilter) => void;
  setDateFilter: (filter: ChatDateFilter) => void;
  selectedActivityFilter: ChatActivityFilter | null;
  selectedDateFilter: ChatDateFilter | null;
};

function FilterMenu(props: FilterMenuProps) {
  const { t } = useTranslation("chat");
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const menuRef = useRef<HTMLDivElement | null>(null);

  function handleClick(ev: React.MouseEvent<HTMLDivElement>) {
    setAnchorEl(anchorEl ? null : ev.currentTarget);
  }

  function handleClickOutside(ev: MouseEvent) {
    if (!anchorEl.contains(ev.target as Node) && !menuRef.current.contains(ev.target as Node)) {
      setAnchorEl(null);
    }
  }

  useEffect(() => {
    if (anchorEl) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [anchorEl]);

  const open = Boolean(anchorEl);
  const id = "filter-menu";

  const getActivityFilterLabel = (filter: string) => {
    switch (filter) {
      case ChatActivityFilter.ALL:
        return t("filterMenu.activityFilters.all");
      case ChatActivityFilter.UNREADS_ONLY:
        return t("filterMenu.activityFilters.unreadOnly");
      case ChatActivityFilter.MENTIONS_ONLY:
        return t("filterMenu.activityFilters.mentionsOnly");
      default:
        return filter;
    }
  };

  const getDateFilterLabel = (filter: string) => {
    switch (filter) {
      case ChatDateFilter.YESTERDAY:
        return t("filterMenu.dateFilters.yesterday");
      case ChatDateFilter.LAST_WEEK:
        return t("filterMenu.dateFilters.lastWeek");
      case ChatDateFilter.LAST_MONTH:
        return t("filterMenu.dateFilters.lastMonth");
      default:
        return filter;
    }
  };

  return (
    <>
      <StyledLazoTooltip arrow placement={open ? "right" : "bottom-start"} title={t("filterMenu.tooltip")}>
        <Box aria-describedby={id} onClick={handleClick} display={"flex"} alignItems={"center"}>
          <FunnelIcon sx={{ fontSize: "1.5rem", ":hover": { cursor: "pointer" } }} />
        </Box>
      </StyledLazoTooltip>
      <StyledPopper id={id} open={open} anchorEl={anchorEl} disablePortal transition>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={100}>
            <PopperContent ref={menuRef}>
              <PopperHeader>
                <Typography>{t("filterMenu.title")}</Typography>
                <StyledCloseOutlined onClick={() => setAnchorEl(null)} />
              </PopperHeader>
              <Box display={"flex"} gap={"0.25rem"} flexDirection={"column"}>
                <Typography fontWeight={"400"} fontSize={"0.75rem"} color={Colors.LIGHT_GRAY_8A8E93}>
                  {t("filterMenu.labels.activity")}
                </Typography>
                <FilterToggleButtonGroup
                  value={props.selectedActivityFilter}
                  onChange={(_, v) => props.setActivityFilter(v || ChatActivityFilter.ALL)}
                  exclusive={true}
                >
                  {Object.keys(ChatActivityFilter).map((k) => (
                    <FilterToggleButton value={k} key={k}>
                      <Typography>{getActivityFilterLabel(k)}</Typography>
                    </FilterToggleButton>
                  ))}
                </FilterToggleButtonGroup>
              </Box>
              <Box display={"flex"} gap={"0.25rem"} flexDirection={"column"}>
                <Typography fontWeight={"400"} fontSize={"0.75rem"} color={Colors.LIGHT_GRAY_8A8E93}>
                  {t("filterMenu.labels.date")}
                </Typography>
                <FilterToggleButtonGroup value={props.selectedDateFilter} onChange={(_, v) => props.setDateFilter(v)} exclusive={true}>
                  {Object.keys(ChatDateFilter).map((k) => (
                    <FilterToggleButton value={k} key={k}>
                      <Typography>{getDateFilterLabel(k)}</Typography>
                    </FilterToggleButton>
                  ))}
                </FilterToggleButtonGroup>
              </Box>
            </PopperContent>
          </Fade>
        )}
      </StyledPopper>
    </>
  );
}

const StyledLazoTooltip = styled(LazoTooltip)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    color: Colors.ELEGANT_BLACK,
    fontSize: "0.875rem",
    fontWeight: "400",
    padding: "0.5rem",
  },
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  padding: "0.75rem",
  boxShadow: "0 -2px 20.1px 0 rgba(101, 101, 101, 0.17)",
  backgroundColor: "#fff",
  borderRadius: "0.75rem",
  zIndex: theme.zIndex.tooltip,
}));

const PopperContent = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  width: "21.25rem",
  gap: "0.75rem",
  [`& .${typographyClasses.root}`]: {
    fontSize: "0.875rem",
    fontWeight: "500",
  },
}));

const PopperHeader = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledCloseOutlined = styled(CloseOutlined)(() => ({
  fontSize: "1rem",
  ":hover": {
    cursor: "pointer",
  },
}));

export default FilterMenu;
